import { z } from "zod";
import { ContactInfoSchema, LivenessSignalSchema, EscalationTimelineSchema, LivenessSignalType, } from "../types/index.js";
// System configuration schema
export const SystemConfigSchema = z.object({
    user: z.object({
        id: z.string(),
        contactInfo: ContactInfoSchema,
        emergencyContacts: z.array(ContactInfoSchema),
    }),
    liveness: z.object({
        requiredSignals: z.array(LivenessSignalSchema),
        checkInterval: z.object({
            value: z.number(),
            unit: z.enum(["seconds", "minutes", "hours", "days"]),
        }),
        gracePeriod: z.object({
            value: z.number(),
            unit: z.enum(["seconds", "minutes", "hours", "days"]),
        }),
        escalationTimelines: z.array(EscalationTimelineSchema),
    }),
    actions: z.object({
        enabled: z.array(z.object({
            name: z.string(),
            enabled: z.boolean(),
            parameters: z.record(z.any()),
            dependencies: z.array(z.string()).optional(),
        })),
        disabled: z.array(z.string()),
    }),
    security: z.object({
        encryptionEnabled: z.boolean(),
        mfaRequired: z.boolean(),
        sessionTimeout: z.object({
            value: z.number(),
            unit: z.enum(["seconds", "minutes", "hours", "days"]),
        }),
    }),
    audit: z.object({
        retentionPeriod: z.object({
            value: z.number(),
            unit: z.enum(["seconds", "minutes", "hours", "days"]),
        }),
        backupFrequency: z.object({
            value: z.number(),
            unit: z.enum(["seconds", "minutes", "hours", "days"]),
        }),
        integrityCheckInterval: z.object({
            value: z.number(),
            unit: z.enum(["seconds", "minutes", "hours", "days"]),
        }),
    }),
});
// Default configuration
export const DEFAULT_CONFIG = {
    user: {
        id: "user-001",
        contactInfo: {
            name: "John Doe",
            email: "john.doe@example.com",
        },
        emergencyContacts: [],
    },
    liveness: {
        requiredSignals: [
            {
                type: LivenessSignalType.ACTIVE_CHECKIN,
                name: "Daily Manual Check-in",
                description: "Manual confirmation that user is alive and well",
                weight: 1.0,
                required: true,
                timeout: { value: 24, unit: "hours" },
            },
        ],
        checkInterval: { value: 1, unit: "hours" },
        gracePeriod: { value: 7, unit: "days" },
        escalationTimelines: [
            {
                name: "Initial Warning",
                delay: { value: 1, unit: "days" },
                actions: ["notify-contacts"],
                requiresConfirmation: false,
            },
            {
                name: "Escalated Response",
                delay: { value: 3, unit: "days" },
                actions: ["notify-contacts"],
                requiresConfirmation: false,
            },
            {
                name: "Final Actions",
                delay: { value: 7, unit: "days" },
                actions: ["notify-contacts"],
                requiresConfirmation: true,
            },
        ],
    },
    actions: {
        enabled: [
            {
                name: "notify-contacts",
                enabled: true,
                parameters: {
                    message: "URGENT: This is an automated notification from the Dead Man's Trigger system. The configured user has not checked in as expected and may require immediate assistance. Please attempt to contact them directly. If you cannot reach them, please follow the emergency protocols established.",
                    priority: "high",
                },
            },
        ],
        disabled: ["release-documents", "shutdown-services", "data-disposal"],
    },
    security: {
        encryptionEnabled: false,
        mfaRequired: false,
        sessionTimeout: { value: 24, unit: "hours" },
    },
    audit: {
        retentionPeriod: { value: 365, unit: "days" },
        backupFrequency: { value: 7, unit: "days" },
        integrityCheckInterval: { value: 1, unit: "days" },
    },
};
//# sourceMappingURL=config-schema.js.map